import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  login as loginAction, 
  logout as logoutAction, 
  checkAuth as checkAuthAction,
  register as registerAction,
  updateProfile as updateProfileAction,
  changePassword as changePasswordAction,
  deleteAccount as deleteAccountAction,
  searchUser as searchUserAction
} from '../store/authSlice';
import authService from '../services/authService';

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isLoading, error, isInitialized } = useSelector((state) => state.auth);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);

  // Check authentication on mount
  useEffect(() => {
    if (!user && !isInitialized) {
      dispatch(checkAuthAction());
    }
  }, [dispatch, user, isInitialized]);

  // Session management effect
  useEffect(() => {
    if (user) {
      // Set initial session info
      const expiry = authService.getSessionExpiry();
      const remaining = authService.getRemainingSessionTime();
      setSessionExpiry(expiry);
      setRemainingTime(remaining);

      // Set up session monitoring
      const sessionCheckInterval = setInterval(() => {
        const currentRemaining = authService.getRemainingSessionTime();
        setRemainingTime(currentRemaining);

        // Check if session is expired
        if (authService.isSessionExpired()) {
          toast.warning('Your session has expired. Please log in again.');
          dispatch(logoutAction());
          navigate('/login');
          return;
        }

        // Warn user when session is about to expire (1 hour before)
        const oneHourInMs = 60 * 60 * 1000;
        if (currentRemaining <= oneHourInMs && currentRemaining > 0) {
          const hoursLeft = Math.ceil(currentRemaining / oneHourInMs);
          toast.info(`Your session will expire in ${hoursLeft} hour${hoursLeft > 1 ? 's' : ''}.`);
        }
      }, 60000); // Check every minute

      // Cleanup interval on unmount
      return () => clearInterval(sessionCheckInterval);
    } else {
      setSessionExpiry(null);
      setRemainingTime(0);
    }
  }, [user, dispatch, navigate]);

  // Login function
  const login = useCallback(async (usernameOrEmail, password) => {
    try {
      await dispatch(loginAction({ usernameOrEmail, password })).unwrap();
      toast.success('Login successful!');
      navigate('/dashboard');
      return { success: true };
    } catch (error) {
      const errorMessage = error || 'Login failed. Please try again.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [dispatch, navigate]);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      await dispatch(registerAction(userData)).unwrap();
      toast.success('Registration successful!');
      navigate('/login');
      return { success: true };
    } catch (error) {
      const errorMessage = error || 'Registration failed. Please try again.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [dispatch, navigate]);

  // Logout function
  const logout = useCallback(() => {
    // Don't show success toast during logout to avoid conflicts with error toasts
    dispatch(logoutAction());
    navigate('/login');
  }, [dispatch, navigate]);

  // Extend session function
  const extendSession = useCallback(() => {
    if (authService.extendSession()) {
      const newExpiry = authService.getSessionExpiry();
      const newRemaining = authService.getRemainingSessionTime();
      setSessionExpiry(newExpiry);
      setRemainingTime(newRemaining);
      toast.success('Session extended successfully!');
      return true;
    }
    return false;
  }, []);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    if (!user || !user.roles) return false;
    return user.roles.includes(role.toUpperCase());
  }, [user]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    if (!user || !user.roles) return false;
    return roles.some(role => user.roles.includes(role.toUpperCase()));
  }, [user]);

  // Check if user has all of the specified roles
  const hasAllRoles = useCallback((roles) => {
    if (!user || !user.roles) return false;
    return roles.every(role => user.roles.includes(role.toUpperCase()));
  }, [user]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return hasRole('ADMIN');
  }, [hasRole]);

  // Check if user is site owner
  const isSiteOwner = useCallback(() => {
    return hasRole('SITE_OWNER');
  }, [hasRole]);

  // Check if user is court owner
  const isCourtOwner = useCallback((courtOwnerId) => {
    return user?.id === courtOwnerId;
  }, [user]);

  // Get user permissions for a specific resource
  const getUserPermissions = useCallback((resourceOwnerId) => {
    if (!user) return { canView: false, canEdit: false, canDelete: false, canManage: false };
    
    const isOwner = user.id === resourceOwnerId;
    const isAdminUser = isAdmin();
    const isSiteOwnerUser = isSiteOwner();

    return {
      canView: true, // Authenticated users can view
      canEdit: isOwner || isAdminUser || isSiteOwnerUser,

      canManage: isOwner || isAdminUser || isSiteOwnerUser
    };
  }, [user, isAdmin, isSiteOwner]);

  // Format remaining time for display
  const formatRemainingTime = useCallback(() => {
    if (remainingTime <= 0) return 'Expired';
    
    const days = Math.floor(remainingTime / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remainingTime % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((remainingTime % (60 * 60 * 1000)) / (60 * 1000));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [remainingTime]);

  // Update profile function
  const updateProfile = useCallback(async (userData) => {
    try {
      await dispatch(updateProfileAction(userData)).unwrap();
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const errorMessage = error || 'Failed to update profile';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [dispatch]);

  // Change password function
  const changePassword = useCallback(async (newPassword) => {
    try {
      await dispatch(changePasswordAction(newPassword)).unwrap();
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const errorMessage = error || 'Failed to change password';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [dispatch]);

  // Delete account function
  const deleteAccount = useCallback(async () => {
    try {
      await dispatch(deleteAccountAction()).unwrap();
      toast.success('Account deleted successfully');
      navigate('/login');
      return { success: true };
    } catch (error) {
      const errorMessage = error || 'Failed to delete account';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [dispatch, navigate]);

  // Search user function
  const searchUser = useCallback(async (identifier) => {
    try {
      const result = await dispatch(searchUserAction(identifier)).unwrap();
      return { success: true, user: result.user };
    } catch (error) {
      const errorMessage = error || 'Failed to search user';
      return { success: false, error: errorMessage };
    }
  }, [dispatch]);

  return {
    // State
    user,
    isLoading,
    error,
    isAuthenticated,
    isInitialized,
    sessionExpiry,
    remainingTime,
    
    // Actions
    login,
    register,
    logout,
    extendSession,
    
    // Account management
    updateProfile,
    changePassword,
    deleteAccount,
    searchUser,
    
    // Role checks
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isAdmin,
    isSiteOwner,
    isCourtOwner,
    
    // Permissions
    getUserPermissions,
    
    // Session utilities
    formatRemainingTime
  };
}; 